import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AR_INTERACT_LIMITS,
  arTargetKey,
  cleanGuardianLine,
  fillGuardianDrafts,
  guardianCell,
  guardianTable,
  normalizeGuardianDrafts,
  withGuardianLine,
  type ArInteract,
  type GuardianScene,
} from "@greg-chick/shared";
import { contentBase, loadManifest, publishAssets, type ManifestEntry } from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { useSceneNames } from "../lib/sceneNames";
import { Msg } from "./ui";

/**
 * 🧑‍🍼 **보호자 문구 한눈에** — 마법 장면 전부의 「보호자에게」 칸을 한 표에서 (2026-10-09).
 *
 * ## 왜
 * 문구는 누르는 자리마다 붙는데, 지금까지는 [📖 전자책] → 책 → 그 쪽 → 🪄 마법 꾸미기 → 누르는 자리로
 * **한 칸씩** 들어가야 했다. 26장면 중 12장면이 남았고(10. 31. 약속) 초안도 있다.
 *
 * ## 지키는 것
 * - 셈은 shared `guardianTable` — [✅ 출시 준비]·`pnpm check:owner` 와 같은 숫자를 말한다.
 * - [초안 넣기]는 **빈 칸에만** 채운다. 게시는 운영자가 읽어 보고 아래 [웹에 올리기]로(묻지 않고 덮지 않는다).
 * - 올릴 때 **게시본을 다시 읽고** 바뀐 칸만 얹는다 — 그 사이 다른 화면이 올린 것을 지우지 않게.
 */
export function GuardianTablePanel() {
  const [table, setTable] = useState<GuardianScene[] | null>(null);
  const [failed, setFailed] = useState(false);
  /** 운영자가 고친 칸(칸 이름 → 글). 게시본과 같아지면 지운다. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [onlyTodo, setOnlyTodo] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  /** 🔴 늦게 온 불러오기가 운영자가 쓰던 칸을 덮지 않게(ArTiltPanel 과 같은 사고) — 고친 칸은 따로 들고 있으니 표만 바꾼다 */
  const loads = useRef(0);

  const load = useCallback(async () => {
    const n = ++loads.current;
    setFailed(false);
    try {
      const m = await loadManifest();
      if (n !== loads.current) return;
      setTable(guardianTable(m.assets));
    } catch {
      // 🔴 「문구가 없다」와 「못 물어봤다」는 다른 말이다
      if (n === loads.current) setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const names = useSceneNames(useMemo(() => (table ?? []).map((s) => s.sceneId), [table]));

  const original = useMemo(() => {
    const o: Record<string, string> = {};
    for (const s of table ?? []) for (const h of s.spots) o[guardianCell(s.sceneId, h.id)] = h.guardian_ko;
    return o;
  }, [table]);
  /** 정말 바뀐 칸만 — 앞뒤 공백만 다른 것은 안 바뀐 것이다(올릴 때 같은 규칙으로 다듬는다) */
  const changed = useMemo(
    () => Object.entries(edits).filter(([cell, v]) => cleanGuardianLine(v) !== cleanGuardianLine(original[cell] ?? "")),
    [edits, original],
  );

  const edit = (cell: string, text: string) => setEdits((e) => ({ ...e, [cell]: text }));

  async function putDrafts() {
    setMsg(null);
    try {
      const r = await fetch(`${contentBase()}/content/guardian-drafts.json`, { cache: "no-cache" });
      if (!r.ok) throw new Error(`초안 파일 ${r.status}`);
      const filled = fillGuardianDrafts(table ?? [], edits, normalizeGuardianDrafts(await r.json()));
      const n = Object.keys(filled).length;
      setEdits((e) => ({ ...e, ...filled }));
      setMsg(
        n
          ? `✏️ 초안 ${n}칸을 빈 자리에 넣었어요. 읽어 보고 고친 뒤 아래 [웹에 올리기]를 눌러 주세요. 아직 아이 화면에는 안 나갔어요.`
          : "넣을 초안이 없어요 — 초안이 있는 칸은 이미 채워져 있어요.",
      );
    } catch (e) {
      setMsg(`⚠️ 초안을 못 불러왔어요: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function save() {
    if (changed.length === 0) return;
    // 🔴 게시 직전에 다시 읽는다 — 게시는 통째로 바꾸기라, 그 사이 다른 화면이 올린 것을 지키려면 최신 판 위에 얹어야 한다
    const m = await loadManifest();
    const next: Record<string, ManifestEntry> = { ...m.assets };
    const lost: string[] = [];
    for (const [cell, text] of changed) {
      const [sceneId, hotspotId] = cell.split("#") as [string, string];
      const key = arTargetKey(sceneId);
      const entry = next[key];
      const def = entry?.interact as ArInteract | undefined;
      const upd = def ? withGuardianLine(def, hotspotId, text) : null;
      if (!entry || !upd) {
        lost.push(names[sceneId] ?? sceneId);
        continue;
      }
      next[key] = { ...entry, interact: upd };
    }
    // 🔴 하나라도 못 얹었으면 올리지 않는다 — 「✅ 올렸어요」인데 일부만 들어가면 아무도 모른다(저장 바 거짓 성공)
    if (lost.length) throw new Error(`그 사이 누르는 자리가 바뀐 장면이 있어요: ${lost.join(" · ")} — [다시 불러오기] 후 다시 써 주세요`);
    const out = await publishAssets(next);
    setEdits({});
    setMsg(`✅ 보호자 문구 ${changed.length}칸을 올렸어요 (v${out.version}). 아이가 그 자리를 누르면 「보호자용」으로 나와요.`);
    await load();
  }

  useRegisterSave({
    id: "guardian",
    label: "보호자 문구",
    dirty: changed.length,
    save,
    // 되살릴 때 글자만 받는다 — 기기에 남은 옛 저장본의 모양을 믿지 않는다
    draft: {
      data: () => edits,
      restore: (d) =>
        setEdits(
          Object.fromEntries(
            Object.entries((d ?? {}) as Record<string, unknown>).filter((kv): kv is [string, string] => typeof kv[1] === "string"),
          ),
        ),
    },
  });

  if (failed)
    return (
      <section className="guardtab">
        <h3>🧑‍🍼 보호자 문구 한눈에</h3>
        <p className="hint">지금 문구를 <b>못 불러왔어요</b>(서버 연결).</p>
        <button type="button" className="btn btn--sm" onClick={() => void load()}>다시 불러오기</button>
      </section>
    );
  if (!table) return <p className="hint">보호자 문구 불러오는 중…</p>;

  const done = table.filter((s) => s.done).length;
  // 쪽 id 와 실제 쪽 차례는 다를 수 있다(《경주》는 p08 이 4쪽) — 이름을 알면 「책 · 쪽 번호」 차례로
  const shown = (onlyTodo ? table.filter((s) => !s.done || s.spots.some((h) => guardianCell(s.sceneId, h.id) in edits)) : table)
    .slice()
    .sort((a, b) => (names[a.sceneId] ?? a.sceneId).localeCompare(names[b.sceneId] ?? b.sceneId, "ko", { numeric: true }));

  return (
    <section className="guardtab">
      <div className="guardtab__head">
        <h3>🧑‍🍼 보호자 문구 한눈에</h3>
        <span className="guardtab__count" data-guardian-count>
          {done}/{table.length}장면 채움
        </span>
      </div>
      <p className="hint">
        아이가 마법 자리를 누르면 화면 아래 「보호자용」으로 접혀 나오는 한 줄이에요. 장면마다 <b>한 칸만 채워도</b>
        그 장면은 된 것으로 세요(「그만」처럼 끄는 자리는 비워 둬도 돼요).
      </p>
      <div className="guardtab__tools">
        <label className="bk-check">
          <input type="checkbox" checked={onlyTodo} onChange={(e) => setOnlyTodo(e.target.checked)} />
          빈 장면만 보기
        </label>
        <button type="button" className="btn btn--sm" onClick={() => void putDrafts()}>
          ✏️ 빈 칸에 초안 넣기
        </button>
        <button type="button" className="btn btn--sm" onClick={() => void load()}>
          다시 불러오기
        </button>
      </div>
      <Msg text={msg} />
      {shown.length === 0 ? (
        <p className="hint">🎉 모든 장면에 보호자 문구가 있어요.</p>
      ) : (
        <ul className="guardtab__list">
          {shown.map((s) => (
            <li key={s.sceneId} className={`guardtab__scene${s.done ? " is-done" : ""}`} data-scene={s.sceneId}>
              <b className="guardtab__where">
                {names[s.sceneId] ?? s.sceneId}
                {s.done ? <span className="guardtab__ok">✓</span> : null}
              </b>
              {s.spots.map((h) => {
                const cell = guardianCell(s.sceneId, h.id);
                const value = edits[cell] ?? h.guardian_ko;
                const id = `guard-${cell.replace(/[^a-z0-9_]/gi, "-")}`;
                return (
                  <div key={h.id} className="guardtab__spot">
                    <label htmlFor={id}>
                      👆 {h.label_ko}
                      {h.child_ko ? <span className="guardtab__child">아이 말풍선: {h.child_ko}</span> : null}
                    </label>
                    <textarea
                      id={id}
                      rows={2}
                      maxLength={AR_INTERACT_LIMITS.say}
                      value={value}
                      placeholder="예: 「누가 먼저 먹었지?」 하고 물어보세요."
                      onChange={(e) => edit(cell, e.target.value)}
                    />
                  </div>
                );
              })}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
